// src/pages/HomePage.jsx

import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";

import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import PublicMapWrapper from "../components/PublicMapWrapper";
import BrandLogo from "../components/BrandLogo";

const MotionBox = motion(Box);

const BRAND_RED = "#E63946";
const BRAND_RED_DARK = "#C92A37";
const BRAND_RED_LIGHT = "#FFF5F5";
const BRAND_BORDER = "#FED7D7";

// -------------------------------------------------------
// HOW IT WORKS
// -------------------------------------------------------

function HowItWorks() {
  const steps = [
    {
      icon: "🔍",
      title: "Search your postcode",
      body: "Search for a postcode sector using the map.",
    },
    {
      icon: "📍",
      title: "Choose a sector",
      body: "Select a postcode sector to view its coupons.",
    },
    {
      icon: "🏷️",
      title: "Browse coupons",
      body: "Browse the coupons available in that postcode sector.",
    },
    {
      icon: "📱",
      title: "Scan your coupon",
      body: "Scan the QR code to view the coupon and its redemption details.",
    },
  ];

  return (
    <Box
      as="section"
      py={{
        base: 9,
        md: 16,
      }}
      bg="white"
      borderTopWidth="1px"
      borderColor="gray.100"
    >
      <Container maxW="container.lg">
        <VStack
          align="stretch"
          spacing={{
            base: 6,
            md: 9,
          }}
        >
          <Box
            maxW="680px"
            mx="auto"
            textAlign="center"
          >
            <Heading
              size={{
                base: "lg",
                md: "xl",
              }}
              color="gray.900"
            >
              How Postcode Coupons works
            </Heading>

            <Text
              mt={3}
              color="gray.600"
              fontSize={{
                base: "sm",
                md: "md",
              }}
              lineHeight="1.7"
            >
              Search a postcode sector, select it on the map and
              browse the coupons available there.
            </Text>
          </Box>

          <SimpleGrid
            columns={{
              base: 1,
              sm: 2,
              lg: 4,
            }}
            spacing={{
              base: 3,
              md: 4,
            }}
          >
            {steps.map((step, index) => (
              <MotionBox
                key={step.title}
                initial={{
                  opacity: 0,
                  y: 18,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  amount: 0.25,
                }}
                transition={{
                  duration: 0.3,
                  delay: index * 0.06,
                }}
                bg="white"
                borderWidth="1px"
                borderColor={BRAND_BORDER}
                borderRadius="xl"
                p={{
                  base: 4,
                  md: 5,
                }}
                boxShadow="sm"
              >
                <Flex
                  align="center"
                  gap={3}
                  mb={3}
                >
                  <Flex
                    width="40px"
                    height="40px"
                    flexShrink={0}
                    align="center"
                    justify="center"
                    borderRadius="full"
                    bg={BRAND_RED_LIGHT}
                    fontSize="lg"
                  >
                    {step.icon}
                  </Flex>

                  <Text
                    color={BRAND_RED}
                    fontSize="xs"
                    fontWeight="extrabold"
                    letterSpacing="wide"
                  >
                    {String(index + 1).padStart(2, "0")}
                  </Text>
                </Flex>

                <Heading
                  size="sm"
                  color="gray.900"
                  mb={2}
                >
                  {step.title}
                </Heading>

                <Text
                  color="gray.600"
                  fontSize="sm"
                  lineHeight="1.6"
                >
                  {step.body}
                </Text>
              </MotionBox>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
}

// -------------------------------------------------------
// HOME PAGE
// -------------------------------------------------------

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <Box
      bg="white"
      minH="100vh"
    >
      {/* SEO */}

      <Helmet>
        <title>
          Postcode Coupons | Find & Place Coupons by Postcode
        </title>

        <meta
          name="description"
          content="Find coupons by postcode sector with Postcode.Coupons. Businesses can place coupons in selected postcode sectors from 1p per household."
        />

        <link
          rel="canonical"
          href="https://postcode.coupons/"
        />

        <meta
          name="robots"
          content="index, follow, max-image-preview:large"
        />

        <meta
          property="og:title"
          content="Postcode Coupons | Find & Place Coupons by Postcode"
        />

        <meta
          property="og:description"
          content="Find coupons by postcode sector. Businesses can place coupons in selected postcode sectors from 1p per household."
        />

        <meta
          property="og:url"
          content="https://postcode.coupons/"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:site_name"
          content="Postcode.Coupons"
        />

        <meta
          name="twitter:card"
          content="summary"
        />

        <meta
          name="twitter:title"
          content="Postcode Coupons | Find & Place Coupons by Postcode"
        />

        <meta
          name="twitter:description"
          content="Find coupons by postcode sector. Businesses can place coupons in selected postcode sectors from 1p per household."
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Postcode.Coupons",
            url: "https://postcode.coupons/",
            description:
              "Find coupons by postcode sector and place business coupons in selected postcode sectors.",
          })}
        </script>
      </Helmet>

      {/* HERO */}

      <Box
        as="section"
        bg="white"
        pt={{
          base: 4,
          md: 7,
        }}
        pb={{
          base: 5,
          md: 8,
        }}
      >
        <Container maxW="container.xl">
          <MotionBox
            initial={{
              opacity: 0,
              y: 12,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.3,
            }}
          >
            <Flex
              direction={{
                base: "column",
                md: "row",
              }}
              align={{
                base: "flex-start",
                md: "center",
              }}
              justify="space-between"
              gap={{
                base: 4,
                md: 10,
              }}
            >
              <Box
                flex="1"
                maxW="720px"
              >
                <Box mb={3}>
                  <BrandLogo size={48} />
                </Box>

                <Heading
                  as="h1"
                  color="gray.900"
                  fontSize={{
                    base: "2xl",
                    sm: "3xl",
                    md: "5xl",
                  }}
                  lineHeight="1.05"
                  letterSpacing="-0.02em"
                  fontWeight="extrabold"
                >
                  Postcode Coupons
                </Heading>

                <Text
                  mt={{
                    base: 2,
                    md: 4,
                  }}
                  maxW="650px"
                  color="gray.700"
                  fontSize={{
                    base: "md",
                    md: "xl",
                  }}
                  lineHeight="1.6"
                >
                  Browse coupons available in your postcode sector.
                </Text>

                <Flex
                  mt={{
                    base: 4,
                    md: 5,
                  }}
                  width="fit-content"
                  maxW="100%"
                  align="center"
                  gap={{
                    base: 4,
                    sm: 5,
                  }}
                  px={{
                    base: 4,
                    sm: 5,
                  }}
                  py={{
                    base: 3,
                    sm: 4,
                  }}
                  borderWidth="2px"
                  borderColor={BRAND_BORDER}
                  borderRadius="xl"
                  bg="white"
                  boxShadow="sm"
                >
                  <Image
                    src="/deliveredby.webp"
                    alt="Delivered by Royal Mail"
                    width={{
                      base: "88px",
                      sm: "104px",
                    }}
                    height="auto"
                    display="block"
                    objectFit="contain"
                    flexShrink={0}
                  />

                  <Text
                    color="gray.800"
                    fontSize={{
                      base: "sm",
                      sm: "md",
                      md: "lg",
                    }}
                    fontWeight="bold"
                    lineHeight="1.35"
                  >
                    Coupons delivered directly to households.
                  </Text>
                </Flex>
              </Box>

              <Box
                display={{
                  base: "none",
                  md: "block",
                }}
                width="300px"
                flexShrink={0}
                borderWidth="1px"
                borderColor={BRAND_BORDER}
                borderRadius="xl"
                bg={BRAND_RED_LIGHT}
                p={5}
              >
                <Text
                  color={BRAND_RED}
                  fontSize="xs"
                  fontWeight="extrabold"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  Start with the map
                </Text>

                <Heading
                  mt={2}
                  size="md"
                  color="gray.900"
                >
                  Search your postcode sector
                </Heading>

                <Text
                  mt={2}
                  color="gray.600"
                  fontSize="sm"
                  lineHeight="1.6"
                >
                  Search or select a sector below to view the
                  coupons available there.
                </Text>
              </Box>
            </Flex>
          </MotionBox>
        </Container>
      </Box>

      {/* POSTCODE MAP */}

      <Box
        as="section"
        bg={BRAND_RED_LIGHT}
        borderTopWidth="1px"
        borderBottomWidth="1px"
        borderColor={BRAND_BORDER}
        py={{
          base: 5,
          md: 8,
        }}
      >
        <Container
          maxW="container.xl"
          px={{
            base: 3,
            sm: 4,
            md: 6,
          }}
        >
          <Box
            mb={{
              base: 3,
              md: 5,
            }}
          >
            <Heading
              size={{
                base: "md",
                md: "lg",
              }}
              color="gray.900"
            >
              Find coupons by postcode
            </Heading>

            <Text
              mt={1.5}
              maxW="650px"
              color="gray.600"
              fontSize="sm"
              lineHeight="1.6"
            >
              Search your postcode sector or select one directly
              from the map.
            </Text>
          </Box>

          <MotionBox
            initial={{
              opacity: 0,
              y: 15,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.35,
            }}
            bg="white"
            borderWidth="1px"
            borderColor={BRAND_BORDER}
            borderRadius="xl"
            boxShadow="md"
            overflow="hidden"
          >
            <Box
              width="100%"
              height={{
                base: "68vh",
                sm: "70vh",
                md: "calc(100vh - 170px)",
              }}
              minHeight={{
                base: "520px",
                md: "650px",
              }}
            >
              <PublicMapWrapper />
            </Box>
          </MotionBox>
        </Container>
      </Box>

      {/* HOW IT WORKS */}

      <HowItWorks />

      {/* BUSINESS CTA */}

      <Box
        as="section"
        py={{
          base: 10,
          md: 16,
        }}
        bg={BRAND_RED}
        color="white"
      >
        <Container maxW="container.md">
          <VStack
            spacing={4}
            textAlign="center"
          >
            <Text
              fontSize="xs"
              fontWeight="extrabold"
              textTransform="uppercase"
              letterSpacing="wide"
              opacity={0.9}
            >
              For businesses
            </Text>

            <Heading
              size={{
                base: "lg",
                md: "xl",
              }}
            >
              Put your coupon on the map
            </Heading>

            <Text
              maxW="620px"
              fontSize={{
                base: "sm",
                md: "md",
              }}
              opacity={0.94}
              lineHeight="1.7"
            >
              Place your coupon in postcode sectors for
              1p per household per sector.
            </Text>

            <Button
              mt={2}
              size="lg"
              bg="white"
              color={BRAND_RED_DARK}
              borderWidth="2px"
              borderColor="white"
              borderRadius="lg"
              fontWeight="extrabold"
              _hover={{
                bg: BRAND_RED_LIGHT,
                transform:
                  "translateY(-1px)",
              }}
              _active={{
                transform:
                  "translateY(0)",
              }}
              onClick={() =>
                navigate(
                  "/coupon-ads"
                )
              }
            >
              Place Your Coupon
            </Button>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
}